import React, { useRef, useState, useEffect } from "react";
import { FaPlay, FaPause, FaVolumeUp, FaVolumeMute, FaCog, FaExpand } from "react-icons/fa";
import "./NewFeedVideoPlayer.css";
import appStore from "store/appStore";
import { Service } from "utils/services";
import { AppLog } from "services";
import { StatusMsg } from "utils";

interface NewFeedVideoPlayerProps {
  src: string;
  thubnail: string;
  attachment: any;
  videoRefCallback: (el: HTMLVideoElement | null) => void;
  autoPlayInView?: boolean;
}

const NewFeedVideoPlayer: React.FC<NewFeedVideoPlayerProps> = ({ src, thubnail, attachment, videoRefCallback, autoPlayInView = false }) => {
  const internalVideoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [controlsVisible, setControlsVisible] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const { isMuted, volume, toggleMute, setGlobalVolume } = appStore();

  useEffect(() => {
    const video = internalVideoRef.current;

    if (video) {
      videoRefCallback(video);
      video.muted = isMuted;
      video.volume = volume;

      const handlePlay = () => setIsPlaying(true);
      const handlePause = () => setIsPlaying(false);

      const handleFullscreenChange = () => {
        setIsFullscreen(!!document.fullscreenElement);
      };

      video.addEventListener("play", handlePlay);
      video.addEventListener("pause", handlePause);

      document.addEventListener("fullscreenchange", handleFullscreenChange);

      return () => {
        video.removeEventListener("play", handlePlay);
        video.removeEventListener("pause", handlePause);
        document.removeEventListener("fullscreenchange", handleFullscreenChange);
      };
    }
  }, [videoRefCallback, isMuted, volume]);

  const togglePlayPause = () => {
    try {
      const video = internalVideoRef.current;
      if (video) {
        if (video.paused) {
          video.play();
        } else {
          video.pause();
        }
      }
    } catch (error) {
      AppLog.show({
        message: `[togglePlayPause] `,
        status: StatusMsg.error,
        error,
      });
    }
  };

  const handleTimeUpdate = () => {
    const video = internalVideoRef.current;
    if (video) setCurrentTime(video.currentTime);
  };

  const handleSpeedChange = () => {
    const newRate = playbackRate >= 2 ? 1 : playbackRate + 0.5;
    setPlaybackRate(newRate);
    if (internalVideoRef.current) {
      internalVideoRef.current.playbackRate = newRate;
    }
  };

  const toggleFullscreen = () => {
    if (internalVideoRef.current?.parentElement) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        internalVideoRef.current.parentElement.requestFullscreen();
      }
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setGlobalVolume(newVolume); // Update global volume
  };

  useEffect(() => {
    if (autoPlayInView && internalVideoRef.current) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          const video = internalVideoRef.current;
          try {
            if (entry.isIntersecting) {
              video?.play();
            } else {
              video?.pause();
            }
          } catch (error) {
            AppLog.show({
              message: `[Playback failed]`,
              status: StatusMsg.error,
              error,
            });
          }
        },
        { threshold: 0.5 }
      );
      observer.observe(internalVideoRef.current);

      return () => observer.disconnect();
    }
  }, [autoPlayInView]);

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };
  const width = Service.toDouble(attachment.attachment_media_width);
  const height = Service.toDouble(attachment.attachment_media_height);
  const ratio = height === 0 ? 1 : width / height;

  return (
    <div
      className={` overflow-hidden relative w-full   group hover:cursor-pointer  ${isFullscreen ? "h-screen w-screen" : ""}   `}
      onMouseEnter={() => setControlsVisible(true)}
      onMouseLeave={() => {
        setTimeout(() => {
          if (isPlaying) setControlsVisible(false);
        }, 300);
      }}
    >
      <div
        className="absolute inset-0 bg-cover bg-center bg-black opacity-50 blur-3xl "
        style={{
          backgroundImage: `url("${thubnail}")`,
        }}
      />
      <video
        ref={internalVideoRef}
        src={src}
        loop
        className={`relative z-10  w-full  ${isFullscreen ? "h-full object-contain" : `max-h-[500px]   ${ratio >= 0 && ratio < 0.74 ? " h-[500px] " : "  "} `}`}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={() => setDuration(internalVideoRef.current?.duration || 0)}
        onClick={togglePlayPause}
        playsInline
        style={{
          pointerEvents: "auto",
        }}
      />

      {!isPlaying && (
        <div className="absolute inset-0 flex items-center justify-center cursor-pointer" onClick={togglePlayPause}>
          <div className="bg-blue-500 bg-opacity-80 rounded-full p-3">
            <FaPlay className="text-white text-2xl" />
          </div>
        </div>
      )}
      {controlsVisible || !isPlaying ? (
        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-30 text-white px-3 py-2 transition-opacity duration-300 opacity-100 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center flex-1">
              <button onClick={togglePlayPause} className="text-xl mr-4 z-20">
                {isPlaying ? <FaPause /> : <FaPlay />}
              </button>
              <input
                type="range"
                min="0"
                max={duration.toString()}
                step="0.1"
                value={currentTime}
                onChange={(e) => {
                  const video = internalVideoRef.current;
                  if (video) {
                    video.currentTime = parseFloat(e.target.value);
                    setCurrentTime(video.currentTime);
                  }
                }}
                className="flex-1 seek-slider h-1 z-20"
                style={{
                  background: `linear-gradient(to right, #007bff ${(currentTime / duration) * 100}%, #ccc ${(currentTime / duration) * 100}%)`,
                }}
              />
            </div>
            <span className="mx-4 text-sm">{`${formatTime(currentTime)} / ${formatTime(duration)}`}</span>
            <div className="flex items-center">
              <button onClick={toggleMute} className="text-xl mr-2 z-20">
                {isMuted ? <FaVolumeMute /> : <FaVolumeUp />}
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={isMuted ? 0 : volume}
                onChange={handleVolumeChange}
                className="w-16 volume-slider h-1 z-20"
                style={{
                  background: isMuted
                    ? `#ccc` // Gray when muted
                    : `linear-gradient(to right, #007bff ${volume * 100}%, #ccc ${volume * 100}%)`,
                }}
              />
            </div>
            <button onClick={handleSpeedChange} className="flex items-center mx-4 text-xl space-x-1 z-20">
              <FaCog />
              <span className="text-sm">{playbackRate}x</span>
            </button>
            <button onClick={toggleFullscreen} className="text-xl z-20">
              <FaExpand />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default NewFeedVideoPlayer;

